import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import SEO from '../components/SEO';
import { Loader2, Eye, EyeOff, Leaf, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function Register() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    company_name: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.company_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await register({
        email: formData.email,
        password: formData.password,
        company_name: formData.company_name,
        role: 'producer_admin'
      });
      toast.success('Account created successfully!');
      navigate('/app');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    'Manage all EPR compliance in one place',
    'Access dedicated account support',
    'Download reports and certificates',
    'Track deadlines and obligations',
  ];

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      <SEO 
        title="Create Account | AfriCycle Zambia"
        description="Register for an AfriCycle Zambia account to start your EPR compliance journey for plastic packaging and WEEE."
        canonicalUrl="https://africycle.pro/register"
        noindex={true}
      />
      {/* Left Panel - Image */}
      <div 
        className="hidden lg:flex flex-1 bg-cover bg-center relative"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1650006883843-c0335fd03c39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxmb3Jlc3QlMjBhZXJpYWwlMjB2aWV3JTIwZ3JlZW58ZW58MHx8fHwxNzcyNDYzNDAwfDA&ixlib=rb-4.1.0&q=85)' 
        }}
      >
        <div className="absolute inset-0 bg-[#056839]/70" />
        <div className="relative z-10 p-12 flex flex-col justify-center text-white">
          <h2 className="text-3xl font-bold mb-6">Join GreenForest Solutions</h2>
          <ul className="space-y-4">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                  <Check className="h-4 w-4" />
                </div>
                <span className="text-white/90">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-white">
        <div className="w-full max-w-md animate-fade-in">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#D2E4D6] mb-4">
              <Leaf className="h-6 w-6 text-[#056839]" />
            </div>
            <h1 className="text-3xl font-bold text-[#056839]">CREATE ACCOUNT</h1>
            <p className="text-[#6B7280] mt-2">Start your compliance journey today</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="company_name" className="text-[#3F3F3F]">Company Name *</Label>
              <Input
                id="company_name"
                name="company_name"
                type="text"
                placeholder="Your Company Ltd"
                value={formData.company_name}
                onChange={handleChange}
                className="mt-1 h-12"
                data-testid="register-company"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-[#3F3F3F]">Email Address *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@company.com"
                value={formData.email}
                onChange={handleChange}
                className="mt-1 h-12"
                data-testid="register-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[#3F3F3F]">Password *</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min. 6 characters"
                  value={formData.password}
                  onChange={handleChange}
                  className="h-12 pr-10"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#3F3F3F]"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-[#3F3F3F]">Confirm Password *</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="mt-1 h-12"
                data-testid="register-confirm-password"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary h-12"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <p className="text-center text-[#6B7280] mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-[#056839] font-medium hover:underline" data-testid="login-link">
              Sign In
            </Link>
          </p>

          <p className="text-center text-xs text-[#6B7280] mt-4">
            By creating an account, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
}
